/**
 * Enhanced Session Tracker for Learning Minutes
 * Tracks incomplete study sessions to support enhanced learning minutes calculation
 */

import { CreateUnitActivityRequest } from '../store/api/umbraco-api';

interface StudySession {
  unitId: number;
  featureId: number;
  startTime: Date;
  isCompleted?: boolean;
}

interface SessionTrackerEvents {
  onSessionStart?: (session: StudySession) => void;
  onSessionEnd?: (session: StudySession & { endTime: Date; duration: number }) => void;
  onWatchActivitySent?: (activityData: CreateUnitActivityRequest & { featureId: number }) => void;
}

class SessionTracker {
  private currentSession: StudySession | null = null;
  private events: SessionTrackerEvents = {};
  private sendWatchActivity: ((featureId: number, activityData: CreateUnitActivityRequest) => Promise<void>) | null = null;

  init(
    sendWatchActivityFn: (featureId: number, activityData: CreateUnitActivityRequest) => Promise<void>,
    events?: SessionTrackerEvents
  ) {
    this.sendWatchActivity = sendWatchActivityFn;
    this.events = events || {};
    this.setupBrowserEventHandlers();
    
    console.log("📊 Session Tracker initialized for enhanced learning minutes");
  }

  startSession(unitId: number, featureId: number): void {
    if (this.currentSession) {
      this.endSession(false);
    }

    const session: StudySession = {
      unitId,
      featureId,
      startTime: new Date(),
    };

    this.currentSession = session;
    
    // DEBUG_SESSION_START: Log session start details
    console.group("🎯 Study Session Started");
    console.log("📊 Session Data:", {
      unitId,
      featureId,
      startTime: session.startTime.toISOString(),
      timestamp: session.startTime.getTime()
    });
    console.groupEnd();

    this.events.onSessionStart?.(session);
  }

  async endSession(completed: boolean = false): Promise<void> {
    if (!this.currentSession) return;

    const endTime = new Date();
    const duration = endTime.getTime() - this.currentSession.startTime.getTime();
    const durationMinutes = duration / 1000 / 60;

    const sessionData = {
      ...this.currentSession,
      endTime,
      duration,
      isCompleted: completed
    };

    // DEBUG_SESSION_END: Log session end details and watch activity decision
    console.group("🏁 Study Session Ended");
    console.log("📊 Session Summary:", {
      unitId: sessionData.unitId,
      featureId: sessionData.featureId,
      startTime: sessionData.startTime.toISOString(),
      endTime: sessionData.endTime.toISOString(),
      durationMs: duration,
      durationMinutes: durationMinutes.toFixed(2),
      completed: completed,
      willSendWatchActivity: !completed && durationMinutes >= 0.5
    });

    if (!completed && durationMinutes >= 0.5) {
      await this.sendWatchActivityForSession(sessionData);
    } else if (completed) {
      console.log("✅ Session completed - unit completion will track this time");
    } else {
      console.log("⏭️ Session too short (<30s) - skipping watch activity");
    }

    console.groupEnd();

    this.events.onSessionEnd?.(sessionData);
    this.currentSession = null;
  }

  markSessionCompleted(): void {
    if (this.currentSession) {
      this.currentSession.isCompleted = true;
      console.log("✅ Session marked as completed - will not send watch activity");
    }
  }

  getCurrentSession(): StudySession | null {
    return this.currentSession;
  }

  private async sendWatchActivityForSession(session: StudySession & { endTime: Date }): Promise<void> {
    if (!this.sendWatchActivity) {
      console.error("❌ Watch activity sender not initialized");
      return;
    }

    const activityData: CreateUnitActivityRequest = {
      unitId: session.unitId,
      startTime: session.startTime.toISOString(),
      endTime: session.endTime.toISOString(),
    };

    try {
      // DEBUG_WATCH_ACTIVITY: Log watch activity API call details
      console.group("📤 Sending Watch Activity");
      console.log("🎯 Activity Data:", {
        featureId: session.featureId,
        unitId: activityData.unitId,
        startTime: activityData.startTime,
        endTime: activityData.endTime,
        durationMinutes: ((session.endTime.getTime() - session.startTime.getTime()) / 1000 / 60).toFixed(2)
      });

      await this.sendWatchActivity(session.featureId, activityData);
      
      console.log("✅ Watch activity sent successfully");
      this.events.onWatchActivitySent?.({ ...activityData, featureId: session.featureId });
      
    } catch (error) {
      console.error("🚨 Watch activity failed:", error);
    }
    
    console.groupEnd();
  }

  private setupBrowserEventHandlers(): void {
    // DEBUG_BROWSER_EVENTS: Handle browser close/refresh and tab switching
    window.addEventListener('beforeunload', (event) => {
      if (this.currentSession && !this.currentSession.isCompleted) {
        const endTime = new Date();
        const duration = endTime.getTime() - this.currentSession.startTime.getTime();
        const durationMinutes = duration / 1000 / 60;

        if (durationMinutes >= 0.5) {
          const activityData = {
            unitId: this.currentSession.unitId,
            startTime: this.currentSession.startTime.toISOString(),
            endTime: endTime.toISOString(),
          };

          console.log("🔄 Browser closing - sending watch activity via beacon");
          
          const payload = JSON.stringify(activityData);
          navigator.sendBeacon(`/activities/${this.currentSession.featureId}`, payload);
        }
      }
    });

    document.addEventListener('visibilitychange', () => {
      if (document.hidden && this.currentSession) {
        console.log("👁️ Page hidden - ending current session");
        this.endSession(false);
      }
    });

    console.log("🔧 Browser event handlers setup for session tracking");
  }

  cleanup(): void {
    if (this.currentSession) {
      this.endSession(false);
    }
  }
}

export const sessionTracker = new SessionTracker();

export const useSessionTracker = () => {
  return {
    startSession: (unitId: number, featureId: number) => sessionTracker.startSession(unitId, featureId),
    endSession: (completed?: boolean) => sessionTracker.endSession(completed),
    markCompleted: () => sessionTracker.markSessionCompleted(),
    getCurrentSession: () => sessionTracker.getCurrentSession(),
  };
}; 